import React, { useState, useEffect } from "react";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import { Button, Grid } from "@mui/material";
import { useUploadImage } from "hook/services";
import { useUpdateAgencyConfig } from "queries/configurator";
import { useAgencyInfo } from "context/agency";
import PropTypes from "prop-types";
import { Delete } from "@mui/icons-material";

function AccountactivationQoute({ loading }) {
    const [agency, Update] = useAgencyInfo();
    const [saving, setSaving] = useState(false);

    const [quotes, setQuotes] = useState([
        { text: "", author_name: "", job_title: "", image: "" },
        { text: "", author_name: "", job_title: "", image: "" },
        { text: "", author_name: "", job_title: "", image: "" }
    ]);

    useEffect(() => {
        if (agency?.account_activation_quote) {
            try {
                const data = JSON.parse(agency.account_activation_quote || "{}");
                const newQuotes = [
                    {
                        text: data.quote1?.text || "",
                        author_name: data.quote1?.author_name || "",
                        job_title: data.quote1?.job_title || "",
                        image: data.quote1?.image || ""
                    },
                    {
                        text: data.quote2?.text || "",
                        author_name: data.quote2?.author_name || "",
                        job_title: data.quote2?.job_title || "",
                        image: data.quote2?.image || ""
                    },
                    {
                        text: data.quote3?.text || "",
                        author_name: data.quote3?.author_name || "",
                        job_title: data.quote3?.job_title || "",
                        image: data.quote3?.image || ""
                    }
                ];
                setQuotes(newQuotes);
            } catch (e) {
                console.error("Failed to parse account_activation_quote", e);
            }
        }
    }, [agency]);

    const uploadImage = useUploadImage();

    const handleQuoteChange = (index, field, value) => {
        const newQuotes = [...quotes];
        newQuotes[index] = { ...newQuotes[index], [field]: value };
        setQuotes(newQuotes);
    };

    const handleImageUpload = async (e, index) => {
        const file = e.target.files[0];
        if (!file) return;

        const response = await uploadImage({
            file: file,
            desiredPath: `design/quote${index + 1}/image`,
        });

        handleQuoteChange(index, "image", response.response.data);
    };

    const saveQuoteMutation = useUpdateAgencyConfig(false);

    const saveQuotes = async () => {
        setSaving(true);
        const data = {
            quote1: quotes[0],
            quote2: quotes[1],
            quote3: quotes[2]
        };

        const payload = {
            account_activation_quote: JSON.stringify(data),
            _id: agency._id
        };

        const { response, error } = await saveQuoteMutation.mutateAsync(payload);
        if (error) {
            console.error("Error updating quotes:", error);
            alert("Error updating quotes");
        } else if (response?.success) {
            Update({ account_activation_quote: JSON.stringify(data) });
        } else {
            alert("Failed to update quotes");
        }
        setSaving(false);
    };


    return (
        <SoftBox p={2}>
            {quotes.map((quote, index) => (
                <SoftBox key={index} mb={5} sx={{ borderBottom: index < 2 ? "1px solid #eee" : "none", pb: index < 2 ? 4 : 0 }}>
                    <SoftTypography variant="h5" fontWeight="bold" mb={2}>
                        Quote {index + 1}
                    </SoftTypography>

                    <SoftBox mb={2} sx={{ position: "relative" }}>
                        <SoftTypography variant="caption" fontWeight="bold">Quote</SoftTypography>
                        <textarea
                            placeholder="Enter quote text..."
                            value={quote.text}
                            onChange={(e) => {
                                const isBackspace = e.nativeEvent.inputType === "deleteContentBackward";
                                if (e.target.value?.length <= 300 || isBackspace) {
                                    handleQuoteChange(index, "text", e.target.value);
                                }
                            }}
                            rows={4}
                            style={{
                                width: "100%",
                                borderRadius: "8px",
                                border: "1px solid #d2d6da",
                                padding: "10px",
                                fontSize: "14px",
                                outline: "none",
                                marginTop: "8px"
                            }}
                        />
                        <span style={{ position: 'absolute', bottom: 12, right: 10, fontSize: "11px" }}>{quote.text?.length || 0}/300</span>
                    </SoftBox>

                    <SoftBox mb={2}>
                        <SoftTypography variant="caption" fontWeight="bold">Quote author name</SoftTypography>
                        <SoftInput
                            type="text"
                            placeholder="Quote author name..."
                            value={quote.author_name}
                            onChange={(e) => handleQuoteChange(index, "author_name", e.target.value)}
                        />
                    </SoftBox>

                    <SoftBox mb={2}>
                        <SoftTypography variant="caption" fontWeight="bold">Author&apos;s Job Title</SoftTypography>
                        <SoftInput
                            type="text"
                            placeholder="Author's job title..."
                            value={quote.job_title}
                            onChange={(e) => handleQuoteChange(index, "job_title", e.target.value)}
                        />
                    </SoftBox>

                    <SoftBox mb={2}>
                        <SoftTypography variant="caption" fontWeight="bold" display="block" mb={1}>Add Image</SoftTypography>
                        <SoftBox display="flex" alignItems="center" border="1px solid #d2d6da" borderRadius="8px" p={1}>
                            <SoftButton
                                variant="gradient"
                                color="dark"
                                component="label"
                                size="small"
                                style={{ marginRight: "10px" }}
                            >
                                Choose file
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={(e) => handleImageUpload(e, index)}
                                />
                            </SoftButton>
                            <SoftTypography variant="caption" color="text">
                                {quote.image ? "Image Uploaded" : "No file chosen"}
                            </SoftTypography>
                        </SoftBox>
                        {quote.image && (
                            <SoftBox mt={1}>
                                <img src={quote.image} style={{ maxWidth: "100px", borderRadius: "4px" }} />
                                <Delete
                                    onClick={() => handleQuoteChange(index, "image", null)}
                                    style={{ cursor: "pointer", marginLeft: "10px" }}
                                />
                            </SoftBox>
                        )}
                    </SoftBox>
                </SoftBox>
            ))}

            {/* Save Button */}
            <SoftBox mt={2} style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={saveQuotes}
                    disabled={loading || saving}
                >
                    {saving ? "Saving..." : "Save Quote Details"}
                </Button>
            </SoftBox>
        </SoftBox>
    );
}

AccountactivationQoute.propTypes = {
    loading: PropTypes.bool,
};

export default AccountactivationQoute;